import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/seo/SEO";
import { toast } from "sonner";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message || "Failed to send reset email");
      return;
    }
    setSent(true);
    toast.success("Check your email for the reset link");
  };

  return (
    <>
      <SEO
        title="Reset your password - QuickMedicalReview"
        description="Reset your QuickMedicalReview password to regain access to your account."
        path="/forgot-password"
      />
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">Reset your password</h1>
            <p className="text-muted-foreground">
              Enter your email and we'll send you a link to set a new password.
            </p>
          </div>

          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle>Forgot password</CardTitle>
              <CardDescription>
                {sent
                  ? "If an account exists for that email, a reset link is on the way."
                  : "We'll email you a secure link to reset your password."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!sent ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={busy}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={busy || !email} className="w-full">
                    {busy ? "Sending..." : "Send reset link"}
                  </Button>
                </form>
              ) : (
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>Didn't get the email? Check your spam folder, or try again in a minute.</p>
                  <Button variant="outline" onClick={() => setSent(false)} className="w-full">
                    Try a different email
                  </Button>
                </div>
              )}
              <div className="mt-6 text-center text-sm">
                <Link to="/auth" className="text-primary hover:underline">
                  Back to sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
